import { useId } from 'react'

/*
 * Small SVG line chart (design/network-console/proto/src/nodecard/LineChart.tsx):
 * fixed 120 px plot, width follows the box (viewBox + preserveAspectRatio="none"
 * for the lines, HTML labels with tabular figures so text never stretches). A
 * null value breaks the line: no report in that step. Series differ by dash,
 * not only by colour. Times are the viewer's zone.
 */

export interface ChartSeries {
  name: string
  values: (number | null)[]
  /** Tailwind stroke class, e.g. stroke-text */
  stroke: string
  dash?: string
}

const H = 120
const W = 600

function linePath(vals: (number | null)[], min: number, max: number): string {
  const n = vals.length
  let d = ''
  let pen = false
  vals.forEach((v, i) => {
    if (v == null) {
      pen = false
      return
    }
    const x = n === 1 ? W / 2 : (i / (n - 1)) * W
    const y = H - ((v - min) / (max - min || 1)) * H
    d += `${pen ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`
    pen = true
  })
  return d
}

function niceMax(v: number): number {
  if (v <= 0) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  const m = v / p
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10) * p
}

const pad = (n: number) => String(n).padStart(2, '0')
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function LineChart({ title, unit, series, t, fmt, min: minIn, max: maxIn, refLine, refLabel, label, now, testId }: {
  title: string
  unit?: string
  series: ChartSeries[]
  /** one time per value, ms */
  t: number[]
  fmt: (v: number) => string
  min?: number
  max?: number
  refLine?: number | number[]
  refLabel?: string
  /** aria-label of the plot */
  label: string
  /** the latest value, right of the title */
  now?: string
  testId?: string
}) {
  const id = useId()
  const all = series.flatMap((s) => s.values.filter((v): v is number => v != null))
  const refs = refLine === undefined ? [] : Array.isArray(refLine) ? refLine : [refLine]
  const lo = minIn ?? Math.min(0, ...all, ...refs)
  const hiRaw = Math.max(...all, ...refs, lo + 1e-9)
  const hi = maxIn ?? (lo < 0 ? Math.max(Math.abs(lo), hiRaw) : niceMax(hiRaw * 1.05))
  const lo2 = lo < 0 && minIn === undefined ? -hi : lo
  const ticks = [hi, (hi + lo2) / 2, lo2]
  const span = t.length ? t[t.length - 1]! - t[0]! : 0
  const tfmt = (ms: number) => {
    const d = new Date(ms)
    return span > 2 * 86_400_000 ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : `${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  const xt = t.length ? [t[0]!, t[Math.floor(t.length / 2)]!, t[t.length - 1]!] : []
  const gaps = series.some((s) => s.values.some((v) => v == null))
  return (
    <figure className="flex min-w-0 flex-col gap-1.5" aria-labelledby={`${id}-t`} data-testid={testId}>
      <figcaption className="flex items-baseline justify-between gap-2">
        <span id={`${id}-t`} className="text-[13px] font-medium text-text-soft">
          {title}
          {unit && <span className="text-muted"> · {unit}</span>}
        </span>
        {now && <span className="num text-xs text-text">{now}</span>}
      </figcaption>
      <div className="grid grid-cols-[52px_minmax(0,1fr)] gap-x-2">
        <div className="relative" style={{ height: H }} aria-hidden>
          {ticks.map((v, i) => (
            <span key={i} className="num absolute right-0 -translate-y-1/2 text-[10.5px] whitespace-nowrap text-muted" style={{ top: `${(i / 2) * 100}%` }}>
              {fmt(v)}
            </span>
          ))}
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} role="img" aria-label={label} className="block overflow-visible">
          {[0, 0.5, 1].map((f) => (
            <line key={f} x1="0" x2={W} y1={f * H} y2={f * H} className="stroke-line" vectorEffect="non-scaling-stroke" />
          ))}
          {refs.map((rv) => {
            const y = H - ((rv - lo2) / (hi - lo2 || 1)) * H
            return <line key={rv} x1="0" x2={W} y1={y} y2={y} className="stroke-warn" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
          })}
          {series.map((s) => (
            <path key={s.name} d={linePath(s.values, lo2, hi)} fill="none" className={s.stroke} strokeWidth="1.5" strokeDasharray={s.dash} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          ))}
        </svg>
        <span />
        <div className="num mt-1 flex justify-between text-[10.5px] text-muted" aria-hidden>
          {xt.map((x, i) => (
            <span key={i}>{tfmt(x)}</span>
          ))}
        </div>
      </div>
      {(series.length > 1 || refLabel || gaps) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-[60px] text-[11px] text-muted">
          {series.length > 1 &&
            series.map((s) => (
              <span key={s.name} className="inline-flex items-center gap-1.5">
                <svg width="16" height="6" aria-hidden>
                  <line x1="0" x2="16" y1="3" y2="3" className={s.stroke} strokeWidth="1.5" strokeDasharray={s.dash} />
                </svg>
                {s.name}
              </span>
            ))}
          {refLabel && (
            <span className="inline-flex items-center gap-1.5">
              <svg width="16" height="6" aria-hidden>
                <line x1="0" x2="16" y1="3" y2="3" className="stroke-warn" strokeDasharray="4 4" />
              </svg>
              {refLabel}
            </span>
          )}
          {gaps && <span>gap = no reports</span>}
        </div>
      )}
    </figure>
  )
}

/** Points with times → one time axis at `stepMs`: a missing step stays null. */
export function onAxis<P extends { t: string }>(points: P[], stepMs: number): { t: number[]; at: (i: number) => P | undefined } {
  if (!points.length) return { t: [], at: () => undefined }
  const ts = points.map((p) => Date.parse(p.t))
  const t0 = Math.min(...ts)
  const t1 = Math.max(...ts)
  const t: number[] = []
  for (let x = t0; x <= t1 && t.length < 5000; x += stepMs) t.push(x)
  const by = new Map<number, P>()
  points.forEach((p, i) => by.set(Math.round((ts[i]! - t0) / stepMs), p))
  return { t, at: (i) => by.get(i) }
}
